import React, { useEffect } from 'react';
import { useAppSelector } from 'src/store';
import { useLoader } from 'src/provider/LoaderProvider';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  useCreateProjectMutation,
  useGetAllProjectsQuery,
  useUpdateProjectMutation,
} from 'src/services';
import { ProductsType } from 'src/constants/products';
import { PATH_ONBOARDING } from 'src/constants/spa-routes';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { projectNameScheme, stringScheme } from 'src/utils/validation-schemes';
import Input from 'src/components/Input/Input';
import { Button } from 'src/components/ui/button';
import { setProject } from 'src/store/slices/onboardingSlice';

const OnboardingStep1 = () => {
  const { project } = useAppSelector((s) => s.onboardingSlice);
  const loader = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: projects } = useGetAllProjectsQuery();
  const currentProject = project.id ? projects?.find((item) => item.id === +project.id) : null;
  const [updateProject, { data: updateProjectRes, isLoading: isUpdateProjectLoading }] =
    useUpdateProjectMutation();
  const [createProject, { data: createProjectRes, isLoading }] = useCreateProjectMutation();
  const {
    handleSubmit,
    control,
    formState: { isValid },
    setValue,
    clearErrors,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      projectName: currentProject ? currentProject.name : '',
    },
    resolver: yupResolver(
      object({
        projectName: projectNameScheme({ required: true }).label('Project Name'),
      }),
    ),
  });

  useEffect(() => {
    if (currentProject && currentProject.id) {
      setValue('projectName', currentProject.name);
    }
  }, [currentProject]);

  const submit = (data) => {
    const request = {
      name: data.projectName,
    };

    if (currentProject && currentProject.id) {
      if (request.name === currentProject.name) {
        navigate(`${PATH_ONBOARDING.ROOT}/2?type=${ProductsType.customer}`, { replace: true });
        return;
      }

      updateProject({
        ...request,
        projectId: currentProject.id,
        cocbi_project_id: currentProject.cocbi_project_id,
        use_cocbi_model: currentProject.use_cocbi_model,
        ai_reply_generation: currentProject.ai_reply_generation,
        ai_text_lense: currentProject.ai_text_lense,
      });
    } else {
      createProject(request);
    }
  };

  useEffect(() => {
    if (isLoading || isUpdateProjectLoading) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoading, isUpdateProjectLoading]);

  useEffect(() => {
    if (createProjectRes) {
      dispatch(setProject(createProjectRes));
      navigate(`${PATH_ONBOARDING.ROOT}/2?type=${ProductsType.customer}`, { replace: true });
    }
  }, [createProjectRes]);

  useEffect(() => {
    if (updateProjectRes) {
      dispatch(setProject(updateProjectRes));
      navigate(`${PATH_ONBOARDING.ROOT}/2?type=${ProductsType.customer}`, { replace: true });
    }
  }, [updateProjectRes]);

  return (
    <div className={'w-full max-w-[450px]'}>
      <form id="create-project-step-form" className="w-full" onSubmit={handleSubmit(submit)}>
        <div className={'input-container w-full'}>
          <Controller
            control={control}
            name="projectName"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'text'}
                placeholder={'Acme AI Agent'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                onFocus={() => {
                  clearErrors('projectName');
                }}
              />
            )}
          />
        </div>
      </form>
      <div className={'mt-5'}>
        <Button
          disabled={!isValid}
          form="create-project-step-form"
          className="w-full py-6 font-semibold"
        >
          <p>Continue</p>
        </Button>
      </div>
    </div>
  );
};

export default OnboardingStep1;
